import { useEffect, useState } from "react";

import { formatUserName } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { Alert, FormField, FormLabel, Modal } from "@impero-com/ui";
import { Select } from "antd";

import {
    EntitiesAccessRightsUpdateScope,
    EntityOverview,
    UpdatableEntitiesAccessRights,
    UserId,
    UserOverview,
} from "api/generated/model";
import { Footer } from "components/modals/Footer";
import { useAllUsersQuery } from "org/control/bulkAssign/queries/useFetchUsersQuery";

import {
    AccessRightChanges,
    computeAccessRightChanges,
} from "./accessRightChanges";
import * as styles from "./AssignUsersModal.css";
import {
    ACCESS_RIGHT_OPTIONS,
    AccessRightOption,
    accessRightLabel,
    scopeLabel,
    toChange,
} from "./labels";

const VISIBLE_DROPDOWN_OPTIONS = 5;
const DROPDOWN_OPTION_HEIGHT = 32;

interface AssignUsersModalProps {
    open: boolean;
    loading: boolean;
    entities: EntityOverview[];
    scopes: EntitiesAccessRightsUpdateScope[];
    onConfirm: (
        update: UpdatableEntitiesAccessRights,
        changes: AccessRightChanges
    ) => void;
    onClose: () => void;
}

export function AssignUsersModal({
    open,
    loading,
    entities,
    scopes,
    onConfirm,
    onClose,
}: AssignUsersModalProps) {
    const { t } = useTranslation();
    const [scope, setScope] = useState<EntitiesAccessRightsUpdateScope>();
    const [userIds, setUserIds] = useState<UserId[]>([]);
    const [accessRight, setAccessRight] = useState<AccessRightOption>();
    const { data: users = [], isLoading: isLoadingUsers } = useAllUsersQuery({
        enabled: open,
    });

    useEffect(() => {
        if (open) {
            /* eslint-disable react-hooks/set-state-in-effect */
            setScope(undefined);
            setUserIds([]);
            setAccessRight(undefined);
            /* eslint-enable react-hooks/set-state-in-effect */
        }
    }, [open]);

    const request =
        scope && accessRight && userIds.length > 0
            ? buildRequest(entities, userIds, scope, accessRight)
            : undefined;
    const isRemoval = accessRight === "NoAccess";

    return (
        <Modal
            variant="content"
            open={open}
            onClose={onClose}
            title={t("ControlEntities_AssignUsers_Title")}
            footer={
                <Footer
                    onClose={onClose}
                    onConfirm={() =>
                        request && onConfirm(request.update, request.changes)
                    }
                    confirmLabel={
                        isRemoval
                            ? t("ControlEntities_AssignUsers_Remove")
                            : t("Confirm")
                    }
                    destructive={isRemoval}
                    loading={loading}
                    okDisabled={
                        request === undefined ||
                        request.changes.entityCountByUser.size === 0
                    }
                />
            }
        >
            <div>
                <div className={styles.selectedEntities}>
                    {t("ControlEntities_AssignUsers_SelectedEntities", {
                        count: entities.length,
                    })}
                </div>
                <FormField fullWidth>
                    <FormLabel>
                        {t("ControlEntities_AssignUsers_Scope")}
                    </FormLabel>
                    <Select
                        className={styles.fullWidth}
                        placeholder={t("Select")}
                        value={scope}
                        onChange={setScope}
                        options={scopes.map((value) => ({
                            value,
                            label: scopeLabel(t, value),
                        }))}
                    />
                </FormField>
                <FormField fullWidth>
                    <FormLabel>
                        {t("ControlEntities_AssignUsers_Users")}
                    </FormLabel>
                    <Select
                        className={styles.fullWidth}
                        mode="multiple"
                        showSearch
                        optionFilterProp="label"
                        maxTagCount="responsive"
                        listHeight={
                            VISIBLE_DROPDOWN_OPTIONS * DROPDOWN_OPTION_HEIGHT
                        }
                        placeholder={t("Select")}
                        loading={isLoadingUsers}
                        value={userIds}
                        onChange={setUserIds}
                        options={users
                            .map((user) => ({
                                value: user.id,
                                label: formatUserName(user),
                            }))
                            .sort((a, b) => a.label.localeCompare(b.label))}
                    />
                </FormField>
                <FormField fullWidth>
                    <FormLabel>
                        {t("ControlEntities_AssignUsers_AccessRights")}
                    </FormLabel>
                    <Select
                        className={styles.fullWidth}
                        placeholder={t("Select")}
                        value={accessRight}
                        onChange={setAccessRight}
                        options={ACCESS_RIGHT_OPTIONS.map((value) => ({
                            value,
                            label: accessRightLabel(t, value),
                        }))}
                    />
                </FormField>
                {request && (
                    <ChangesFeedback
                        changes={request.changes}
                        userIds={userIds}
                        users={users}
                        isRemoval={isRemoval}
                    />
                )}
            </div>
        </Modal>
    );
}

function buildRequest(
    entities: EntityOverview[],
    userIds: UserId[],
    scope: EntitiesAccessRightsUpdateScope,
    accessRight: AccessRightOption
) {
    const change = toChange(accessRight);
    const update: UpdatableEntitiesAccessRights = {
        entityIds: entities.map((entity) => entity.id),
        userIds,
        scope,
        change,
    };
    return {
        update,
        changes: computeAccessRightChanges(entities, userIds, scope, change),
    };
}

interface ChangesFeedbackProps {
    changes: AccessRightChanges;
    userIds: UserId[];
    users: UserOverview[];
    isRemoval: boolean;
}

function ChangesFeedback({
    changes,
    userIds,
    users,
    isRemoval,
}: ChangesFeedbackProps) {
    const { t } = useTranslation();
    if (changes.entityCountByUser.size === 0) {
        return (
            <Alert
                type="info"
                showIcon
                message={
                    isRemoval
                        ? t(
                              "ControlEntities_AssignUsers_NothingToRemove_Title",
                              {
                                  count: userIds.length,
                              }
                          )
                        : t(
                              "ControlEntities_AssignUsers_NothingToGrant_Title",
                              {
                                  count: userIds.length,
                              }
                          )
                }
                description={t(
                    "ControlEntities_AssignUsers_NothingToChange_Description",
                    { count: userIds.length }
                )}
            />
        );
    }

    const usersById = new Map(users.map((user) => [user.id, user]));
    return (
        <div className={styles.summary}>
            {userIds.map((userId) => {
                const count = changes.entityCountByUser.get(userId) ?? 0;
                return (
                    <div key={userId} className={styles.summaryRow}>
                        <span>{formatUserName(usersById.get(userId))}</span>
                        <span className={styles.summaryCount}>
                            {isRemoval
                                ? t(
                                      "ControlEntities_AssignUsers_Summary_Remove",
                                      { count }
                                  )
                                : t("ControlEntities_AssignUsers_Summary_Add", {
                                      count,
                                  })}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}
